/**
 * La forma de la sesión y de los errores, compartida entre la SPA y el servidor de juego.
 *
 * Se define una sola vez (principio II): una copia divergente del esquema se manifestaría como
 * un fallo en producción, no como un error de compilación.
 */
import { z } from 'zod';
import { LOCALES, ROLES } from './constants.js';
/**
 * Marca de tiempo tal como la serializa PostgREST.
 *
 * `z.iso.datetime()` sin opciones exige el sufijo `Z`, pero PostgREST emite el desplazamiento
 * explícito (`2026-08-18T10:00:00.123456+00:00`). Sin `offset: true` se rechazarían **todas** las
 * filas reales. Se define aquí una vez porque el problema se repite en cada tabla.
 */
export const timestamptz = z.iso.datetime({ offset: true });
export const roleSchema = z.enum(ROLES);
export const localeSchema = z.enum(LOCALES);
/** El perfil tal como lo ve su propietario. */
export const sessionProfileSchema = z.object({
    id: z.uuid(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    role: roleSchema,
    locale: localeSchema,
    soundEnabled: z.boolean(),
});
/** Cuerpo de la llamada que arranca la sesión. */
export const bootstrapRequestSchema = z.object({
    /** Idioma detectado en el navegador. Solo se usa si el perfil se crea en esta llamada. */
    detectedLocale: localeSchema.optional(),
});
export const sessionResponseSchema = z.object({
    profile: sessionProfileSchema,
    /** Verdadero solo la primera vez que esta persona entra en Clica. */
    created: z.boolean(),
});
/** El perfil tal como lo ve quien administra. */
export const adminUserSchema = z.object({
    id: z.uuid(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    role: roleSchema,
    bannedAt: timestamptz.nullable(),
    createdAt: timestamptz,
    lastSeenAt: timestamptz.nullable(),
    /** Verdadero para la cuenta responsable: no se le puede retirar el acceso. */
    isPlatformOwner: z.boolean(),
});
export const adminUsersResponseSchema = z.object({ users: z.array(adminUserSchema) });
/** Un cuerpo vacío es válido: no cambia nada, y eso no es un error. */
export const updateUserRequestSchema = z.object({
    role: roleSchema.optional(),
    banned: z.boolean().optional(),
});
/**
 * Claves de error del protocolo.
 *
 * Viajan como clave de traducción, nunca como texto ya traducido: el idioma lo resuelve el
 * cliente, y el proyector del aula y el móvil del alumno pueden estar en idiomas distintos
 * (principio V).
 */
export const API_ERROR_KEYS = [
    'auth.error.unauthenticated',
    'auth.error.domain_not_allowed',
    'auth.error.banned',
    'auth.error.forbidden',
    'admin.error.cannot_change_own_role',
    'admin.error.cannot_ban_self',
    'admin.error.cannot_ban_platform_owner',
    'admin.error.user_not_found',
    'game.error.quiz_not_found',
    'game.error.game_not_found',
    'game.error.quiz_not_playable',
    'game.error.no_pin_available',
    'account.error.last_admin',
];
export const apiErrorSchema = z.object({
    error: z.object({
        key: z.enum(API_ERROR_KEYS),
        params: z.record(z.string(), z.string()).optional(),
    }),
});
/** Estado HTTP que corresponde a cada clave de error. */
export const API_ERROR_STATUS = {
    'auth.error.unauthenticated': 401,
    'auth.error.domain_not_allowed': 403,
    'auth.error.banned': 403,
    'auth.error.forbidden': 403,
    'admin.error.cannot_change_own_role': 409,
    'admin.error.cannot_ban_self': 409,
    'admin.error.cannot_ban_platform_owner': 409,
    'admin.error.user_not_found': 404,
    'game.error.quiz_not_found': 404,
    'game.error.game_not_found': 404,
    'game.error.quiz_not_playable': 409,
    // No es culpa de quien pide: se han agotado los diez mil códigos. Reintentar más tarde sí sirve.
    'game.error.no_pin_available': 503,
    // Sin la última cuenta que administra no queda nadie que pueda hacerlo, y recuperarla exige
    // entrar en la base de datos a mano.
    'account.error.last_admin': 409,
    // No se distingue «no existe» de «no es de Clica»: decirlo permitiría averiguar qué correos
    // tienen cuenta probándolos uno a uno.
};
//# sourceMappingURL=session-contract.js.map